import type {
  CrossroadsLog,
  GroupMetadata,
  LogOverviewRow,
} from '@/api/hooks/useIntegrationLogs'
import type { CellClickedEvent } from 'ag-grid-community'
import { useCallback, useState } from 'react'
import {
  EXPAND_COL_ID,
  FIRST_ISSUE_COL_ID,
  LATEST_DETAILS_COL_ID,
  LATEST_EVENT_COL_ID,
  LATEST_ISSUE_COL_ID,
  SILENCING_COL_ID,
} from './LogsColumnDefs'

/** Which of the row's logs each column opens in the details sidebar. Every
 * other column just expands/collapses the row's detail grid. */
const SIDEBAR_LOG_BY_COL_ID: Record<
  string,
  (row: LogOverviewRow) => CrossroadsLog | null | undefined
> = {
  [LATEST_EVENT_COL_ID]: (row) => row.latest_log,
  [LATEST_DETAILS_COL_ID]: (row) => row.latest_log,
  [LATEST_ISSUE_COL_ID]: (row) => row.last_open_issue,
  [FIRST_ISSUE_COL_ID]: (row) => row.first_open_issue,
}

/**
 * Selection state shared by every log overview grid's details sidebar —
 * clicking a Latest Event / Latest Log Details / Latest Issue / First Issue
 * cell opens that log in the sidebar; clicking anywhere else on the row
 * expands or collapses its detail grid instead. A sidebar column whose log
 * is absent (e.g. a row with no open issue) falls back to expanding too, so
 * an empty cell still does something. None of this reads order-specific
 * fields, so it works for any LogOverviewRow.
 */
export function useLogSidebar() {
  const [selectedLog, setSelectedLog] = useState<CrossroadsLog | null>(null)
  const [selectedLogGroupId, setSelectedLogGroupId] = useState<string | null>(
    null,
  )
  const [selectedGroupMetadata, setSelectedGroupMetadata] =
    useState<GroupMetadata | null>(null)

  const selectLog = useCallback(
    (
      log: CrossroadsLog,
      groupId: string | null,
      /** Omit to leave whatever the last selection set -- the detail grid's
       * rows all belong to the master row that was clicked to expand them, so
       * its metadata is still the right one. */
      metadata?: GroupMetadata | null,
    ) => {
      setSelectedLog(log)
      setSelectedLogGroupId(groupId)
      if (metadata !== undefined) setSelectedGroupMetadata(metadata)
    },
    [],
  )

  const onCellClicked = useCallback(
    (event: CellClickedEvent<LogOverviewRow>) => {
      const row = event.data
      if (!row) return
      const colId = event.column.getColId()
      // The chevron's own cell renderer already toggles the row -- toggling
      // again here would cancel it out.
      if (colId === EXPAND_COL_ID) return
      // The silencing menu opens its own dropdown -- expanding the row as
      // well would shove the grid around underneath it.
      if (colId === SILENCING_COL_ID) return
      const log = SIDEBAR_LOG_BY_COL_ID[colId]?.(row)
      if (!log) {
        event.node.setExpanded(!event.node.expanded)
        return
      }
      setSelectedLog(log)
      setSelectedLogGroupId(row.id)
      setSelectedGroupMetadata(row.metadata)
    },
    [],
  )

  const clearSelectedLog = useCallback(() => setSelectedLog(null), [])

  return {
    selectedLog,
    selectedLogGroupId,
    selectedGroupMetadata,
    selectLog,
    onCellClicked,
    clearSelectedLog,
  }
}
